import type { CSSProperties, KeyboardEvent, ReactNode } from "react";

export interface ListColumn<Row> {
  key: string;
  header: string;
  /**
   * title: название, на телефоне первой строкой карточки; text: прочий
   * текст, на телефоне приглушённо под названием; number: число справа.
   */
  kind: "title" | "text" | "number";
  /** Фиксированная ширина колонки, например "8rem". У текста по умолчанию остаток. */
  width?: string;
  render: (row: Row) => ReactNode;
}

export interface ListTableProps<Row> {
  columns: ListColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Подпись таблицы для читалки. */
  label: string;
  empty?: ReactNode;
  onRowClick?: (row: Row) => void;
}

/**
 * Таблица-список: на ПК строки с колонками, на телефоне карточки. Ширины
 * колонок фиксированные, а не auto (docs/ui-rules.md, «3»): иначе у каждой
 * строки своя сетка, и числа соседних строк не кончаются на одной вертикали.
 */
export function ListTable<Row>({ columns, rows, rowKey, label, empty, onRowClick }: ListTableProps<Row>) {
  const cols = columns
    .map((c) => c.width ?? (c.kind === "number" ? "8rem" : "minmax(0, 1fr)"))
    .join(" ");
  const style = { "--w-list-cols": cols } as CSSProperties;
  const firstNumber = columns.find((c) => c.kind === "number")?.key;

  const onKey = (row: Row) => (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onRowClick?.(row);
    }
  };

  return (
    <div className="w-list" role="table" aria-label={label} style={style}>
      <div className="w-list-row" role="row" data-head="">
        {columns.map((c) => (
          <div key={c.key} role="columnheader" className="w-list-cell" data-kind={c.kind}>
            {c.header}
          </div>
        ))}
      </div>
      {rows.length === 0 && empty ? <div className="w-list-empty">{empty}</div> : null}
      {rows.map((row) => (
        <div
          key={rowKey(row)}
          role="row"
          className="w-list-row"
          data-interactive={onRowClick ? "" : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          onKeyDown={onRowClick ? onKey(row) : undefined}
        >
          {columns.map((c) => (
            <div
              key={c.key}
              role="cell"
              className="w-list-cell"
              data-kind={c.kind}
              data-compact-label={c.kind === "number" && c.key !== firstNumber ? c.header : undefined}
            >
              {c.render(row)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
